import type { AxiosInstance } from 'axios';
import type { DocumentSnapshot, ActiveClassifications } from '../types/snapshot';
import type { MyReviewInfo } from '../types/review';
import type { PaginatedResponse, PaginationParams } from '../types/common';

export function createSnapshotService(client: AxiosInstance) {
  return {
    /** POST /v1/document-snapshots/search — FilterRequest body + Spring Pageable query params */
    search: (params: PaginationParams & { status?: string }) => {
      const filters: Record<string, unknown> = {};
      if (params.status) filters.status = params.status;

      const body: Record<string, unknown> = {};
      if (Object.keys(filters).length > 0) body.filters = filters;
      if (params.search) body.search = params.search;

      return client.post<PaginatedResponse<DocumentSnapshot>>(
        '/v1/document-snapshots/search',
        Object.keys(body).length > 0 ? body : undefined,
        { params: { page: params.page, size: params.size, sort: params.sort } },
      ).then(r => r.data);
    },

    /** GET /v1/document-snapshots/{id} — ApiResponse wrapped */
    getById: (id: string) =>
      client.get<DocumentSnapshot>(`/v1/document-snapshots/${id}`).then(r => r.data),

    /**
     * GET /v1/document-snapshots/{id}/editor-config — raw OnlyOffice config (NOT ApiResponse wrapped)
     * @param mode Optional — 'view' for read-only preview, 'review' for tracked-changes mode.
     *             Backend returns permission + docKey appropriate to the mode.
     */
    getEditorConfig: (id: string, mode?: string) =>
      client.get<Record<string, unknown>>(`/v1/document-snapshots/${id}/editor-config`, {
        params: mode ? { mode } : undefined,
      }).then(r => r.data),

    /**
     * GET /v1/document-snapshots/{id}/my-review — resolves the logged-in user's
     * reviewer identity on this snapshot's active review. 404 if the caller is
     * not a registered reviewer (neither INTERNAL nor EXTERNAL).
     */
    getMyReview: (id: string) =>
      client.get<MyReviewInfo>(`/v1/document-snapshots/${id}/my-review`).then(r => r.data),

    /** GET /v1/document-snapshots/active-classifications — ApiResponse wrapped */
    getActiveClassifications: () =>
      client.get<ActiveClassifications>('/v1/document-snapshots/active-classifications').then(r => r.data),
  };
}
